import { Assignation } from '../../../context/types'
import { getStatements } from '../../../i18n/Statements'
import { defaultParamsFitme } from '../../../parameters/defaultParamsFitme'
import { logException } from '../../../services/sentryLogger'
import { isMobilePortrait } from '../../../utils'
import { DIMENSIONS_TYPE } from '../../../utils/values/cats'

type ScreenParams = {
    nCol: number
    stmPerCol: number
    nStm: number
}

/**
 * parseScreenParam("M=5x1;D=10x2", isMobile) === { nCol = isMobile ? 1 : 2; stmPerCol =  isMobile ? 5:10; nStm = isMobile ? 5*1 : 10*2 }
 * @param input
 * @param isMobileFlag
 * @returns
 */

export function parseScreenParam(
    input: string,
    isMobileFlag: boolean = isMobilePortrait()
): ScreenParams {
    if (!input || typeof input !== 'string') {
        logException(
            'parseScreenParam',
            'Invalid input for screen parameters: ' + input + '/' + isMobileFlag
                ? ' mobile'
                : ' desktop'
        )
        input = defaultParamsFitme.assessmentParams['stmsPerScreen']
    }

    try {
        const parts = input.split(';')
        const map: Record<string, [number, number]> = {}

        for (const part of parts) {
            const [prefix, values] = part.split('=')
            if (!prefix || !values) continue
            const [stm, col] = values.split('x').map(Number)
            if (isNaN(stm) || isNaN(col)) continue
            map[prefix] = [stm, col]
        }

        const key = isMobileFlag ? 'M' : 'D'
        const parsedSelection = map[key]

        if (parsedSelection) {
            return {
                stmPerCol: parsedSelection[0],
                nCol: parsedSelection[1],
                nStm: parsedSelection[0] * parsedSelection[1],
            }
        }

        logException(
            'parseScreenParam',
            'Invalid input for screen parameters: ' + input + '/' + isMobileFlag
                ? ' mobile'
                : ' desktop'
        )
        return parseScreenParam(
            defaultParamsFitme.assessmentParams['stmsPerScreen'],
            isMobileFlag
        )
    } catch {
        logException(
            'parseScreenParam',
            'Invalid input for screen parameters: ' + input + '/' + isMobileFlag
                ? ' mobile'
                : ' desktop'
        )
        return parseScreenParam(
            defaultParamsFitme.assessmentParams['stmsPerScreen'],
            isMobileFlag
        )
    }
}

// TODO
export function isEdgeBrowser(): boolean {
    return /Edg\/\d+/.test(navigator.userAgent) // Detects Chromium-based Edge
}

//   [${stm.dimensionKey}][${stm.valueKey}][${stm.diversityScore}]
const shortDimMap: Record<DIMENSIONS_TYPE, string> = {
    'Social Values': 'SOCI',
    'Growth & Flexibility': 'GROW',
    Conservativeness: 'CONS',
    Quality: 'QUAL',
    Recognition: 'RECO',
    Power: 'POW',
}
export const debugStm = (stm: Assignation) =>
    `[${shortDimMap[stm.dimensionKey as DIMENSIONS_TYPE]}][${stm.valueKey}](${
        stm.diversityScore
    })`

export const debugStms = (stms: Assignation[]) => stms.map(debugStm)

/**
 *
 */
type InputType = Object | Object[] | string | number | boolean
const isAssignation = (obj: any): obj is Assignation => 'dimensionKey' in obj
// const isAssignationArray = (obj: any): obj is Assignation[] => Array.isArray(obj) && obj.length > 0 && isAssignation(obj[0]);
export const debugStmsDeep = (obj: InputType): InputType => {
    if (Array.isArray(obj)) {
        // Check if array contains objects with dimensionKey
        if (
            obj.length > 0 &&
            typeof obj[0] === 'object' &&
            obj[0] !== null &&
            isAssignation(obj[0])
        ) {
            return obj.map((stm) => debugStm(stm as Assignation))
        }
        return obj.map(debugStmsDeep)
    } else if (typeof obj === 'object' && obj !== null) {
        if (isAssignation(obj)) {
            return debugStm(obj as Assignation)
        }
        return Object.entries(obj).reduce(
            (acc, [key, value]) => {
                acc[key] = debugStmsDeep(value)
                return acc
            },
            {} as Record<string, InputType>
        )
    } else {
        // Primitive value
        return obj
    }
}

// Polyfill for Array.prototype.toSpliced
// TS2551: Property 'toSpliced' does not exist on type 'Assignation[]'. Did Dou mean 'splice'?
export const toSpliced = <T>(arr: T[], index: number, amountToDelete = 1, ...items: T[]) => {
    return [...arr.slice(0, index), ...items, ...arr.slice(index + amountToDelete)] as T[]
}

export const byDiversityAsc = (a: Assignation, b: Assignation): number =>
    a.diversityScore! - b.diversityScore!

export const byDiversityDesc = (a: Assignation, b: Assignation): number =>
    b.diversityScore! - a.diversityScore!


const genFakeBuckets = () => {
    const statements = getStatements()
    const buckets: Assignation[][] = Array(9)
        .fill([])
        .map(() => [])
    let currentIndex = 0
    const bucketSizes = [2, 4, 5, 6, 8, 6, 5, 4, 2]
    bucketSizes.forEach((size, bucketIndex) => {
        buckets[bucketIndex] = statements.slice(
            currentIndex,
            currentIndex + size
        )
        currentIndex += size
    })
    return buckets
}